import React from 'react';
import { errorResponseNotify } from '#modules/utils/errors';
import useLiquidNobleApi from '#modules/utils/useLiquidNobleApi';

const JWT_REFRESH_PROXIMITY = 300; // 5 minutes from expiration
let sessionAuthToken; // Memory fallback for devices without localStorage suppport (e.g. incognito mode)
let sessionUsername;
let sessionMetadata = {};

export function saveSessionMetadata(metadata) {
  sessionMetadata = metadata;
  if (metadata === null) {
    localStorage?.removeItem('sessionMetadata');
    return;
  }
  localStorage?.setItem('sessionMetadata', JSON.stringify(metadata));
}

export function getSessionMetadata() {
  if (Object.keys(sessionMetadata ?? {}).length) {
    return sessionMetadata;
  }
  const metadata = localStorage?.getItem('sessionMetadata');
  return metadata ? JSON.parse(metadata) : {};
}

export function addToSessionMetadata(key, value) {
  const metadata = getSessionMetadata();
  metadata[key] = value;
  saveSessionMetadata(metadata);
}

export function saveProfileId(profileId) {
  addToSessionMetadata('profileId', profileId);
}

export function saveLockboxId(lockboxId) {
  addToSessionMetadata('lockboxId', lockboxId);
}

export function saveUnlockingKey(value) {
  addToSessionMetadata('unlockingKey', value);
}

export function saveEntityId(entityId) {
  addToSessionMetadata('entityId', entityId);
}

export function getSessionProfileId() {
  return getSessionMetadata().profileId;
}

export function getSessionEntityId() {
  return getSessionMetadata().entityId;
}

export function getSessionLockboxId() {
  return getSessionMetadata().lockboxId;
}

export function getSessionUnlockingKey() {
  return getSessionMetadata().unlockingKey;
}

export function clearSessionMetadata() {
  saveSessionMetadata(null);
}

export function saveAuthToken(authToken) {
  sessionAuthToken = authToken;
  if (authToken === null) {
    localStorage?.removeItem('authToken');
    return;
  }
  localStorage?.setItem('authToken', authToken);
}

export function getSessionUsername() {
  if (sessionUsername) {
    return sessionUsername;
  }
  return localStorage?.getItem('sessionUsername');
}

export function saveSessionUsername(username) {
  sessionUsername = username;
  if (username === null) {
    localStorage?.removeItem('sessionUsername');
    return;
  }
  localStorage?.setItem('sessionUsername', username);
}

export function getAuthToken() {
  if (getTokenizedAuthToken()) {
    // Do not allow to sign in as LQNBP in parallel to Tokenized
    return null;
  }

  if (sessionAuthToken) {
    return sessionAuthToken;
  }
  return localStorage?.getItem('authToken');
}

export function getTokenizedAuthToken() {
  return sessionStorage?.getItem('authToken');
}

export function isLiquidNobleLoggedIn() {
  return !!getAuthToken();
}

export function getJwtExpiration(token) {
  if (!token) {
    return 0;
  }
  const { payload } = parseJwt(token);
  return payload.exp;
}

export function shouldRefreshToken(token) {
  // Get the expiration time of the token
  const expirationTime = getJwtExpiration(token);
  if (!expirationTime) {
    return true;
  }
  const currentTime = Math.floor(Date.now() / 1000);
  const expiresIn = expirationTime - currentTime;

  return expiresIn < JWT_REFRESH_PROXIMITY;
}

export function hasJwtExpired(token) {
  const expirationTime = getJwtExpiration(token);
  const currentTime = Math.floor(Date.now() / 1000);
  return expirationTime < currentTime;
}

export function parseJwt(token) {
  if (!token) {
    return null;
  }
  // Split the token into its three parts
  const [headerBase64Url, payloadBase64Url, signature] = token.split('.');

  // Function to decode Base64URL (replace characters, add padding, and decode)
  const base64UrlToBase64 = (base64Url) => {
    return base64Url.replace(/-/g, '+').replace(/_/g, '/');
  };

  // Decode and parse the header and payload
  const header = JSON.parse(atob(base64UrlToBase64(headerBase64Url)));
  const payload = JSON.parse(atob(base64UrlToBase64(payloadBase64Url)));

  // Return the decoded header, payload, and signature
  return { header, payload, signature };
}

export function useJWTRefreshCheck(executeInstantly = true) {
  const liquidNobleApi = useLiquidNobleApi();

  const onRefreshJWT = React.useCallback(
    async (existingToken) => {
      try {
        const response = await liquidNobleApi.post('/user/jwt/refresh/');
        const { data } = response;
        if (data?.token) {
          saveAuthToken(data.token);
        }
      } catch (error) {
        if (hasJwtExpired(existingToken)) {
          saveAuthToken(null);
          clearSessionMetadata();
          window?.location?.reload();
        }
      }
    },
    [liquidNobleApi],
  );

  const performCheckAndRefresh = React.useCallback(() => {
    const token = getAuthToken();
    if (!token) {
      return;
    }
    if (!shouldRefreshToken(token)) {
      return;
    }
    onRefreshJWT(token);
  }, [onRefreshJWT]);

  React.useEffect(() => {
    if (executeInstantly) {
      performCheckAndRefresh();
    }
  }, [executeInstantly, performCheckAndRefresh]);

  return performCheckAndRefresh;
}
